// src/receptionist/screens/LearnFromChats.tsx
//
// Upload real WhatsApp threads, and the AI proposes the flow, tone, languages
// and FAQ notes it can see the merchant already using. Nothing is applied
// until the merchant ticks it: a mis-read price would otherwise be repeated to
// every customer from then on.
//
// Wired to the transcript API (ourteam-frontend#3610, BE ourteam-backend#4672).
// The privacy contract stated on this screen is binding on the backend: used
// once to configure this merchant, never for model training, customer names
// and numbers redacted on ingest, deleted after TRANSCRIPT_RETENTION_DAYS.
// This screen also offers the immediate delete, so a merchant with second
// thoughts does not have to wait for the retention job.

import { useEffect, useRef, useState } from "react";
import { AlertTriangleIcon, CheckCircle2Icon, FileTextIcon, Loader2Icon, MessageCircleIcon, ShieldCheckIcon, SparklesIcon, Trash2Icon, UploadIcon, XIcon } from "lucide-react";
import { PageHeader, Card, CardHeader, CardBody, Button } from "../components/kit";
import { cn } from "@oura/lib/cn";
import { Checkbox } from "@component/checkbox";
import { TagPill } from "../components/ui";
import { useSnackbar } from "@hook/use-snackbar";
import {
    uploadTranscripts, analyseTranscripts, getAnalysisJob, applyProposals, deleteTranscripts,
    type Proposal, type TranscriptFile, type MerchantConfig, type BookingFlow,
} from "@service/receptionist";
import { PROPOSAL_KIND_LABELS, TRANSCRIPT_RETENTION_DAYS } from "../transcripts";

/** What an upload actually buys the merchant, said before they commit files. */
const WHAT_IT_FINDS = [
    { icon: MessageCircleIcon, title: "The order you ask things in", body: "The steps your team already follows, turned into a booking flow." },
    { icon: SparklesIcon, title: "How you sound", body: "Greeting, tone and the languages you actually reply in." },
    { icon: FileTextIcon, title: "The answers you repeat", body: "Prices, policies and the questions that come up every week." },
] as const;

const POLL_MS = 3000;

/** All three bands, each its own colour. `medium` is the one that used to fall
 *  through to nothing. */
const CONFIDENCE_STYLE: Record<Proposal["confidence"], string> = {
    high: "bg-emerald-100 text-emerald-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-gray-200 text-gray-600",
};
const CONFIDENCE_LABEL: Record<Proposal["confidence"], string> = {
    high: "Clear pattern",
    medium: "Worth checking",
    low: "Check carefully",
};

type Analysis =
    | { kind: "idle" }
    | { kind: "running"; jobId: string | null }
    | { kind: "done"; threadCount: number; proposals: Proposal[] }
    | { kind: "applied"; merchant: MerchantConfig; flow: BookingFlow; count: number };

export function LearnFromChatsScreen({ merchantId }: { merchantId: string | null }) {
    const snackbar = useSnackbar();
    const [files, setFiles] = useState<TranscriptFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [analysis, setAnalysis] = useState<Analysis>({ kind: "idle" });
    const [problems, setProblems] = useState<string[]>([]);
    const [applying, setApplying] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const pollJob = useRef<string | null>(null);

    const addFiles = async (list: FileList | null) => {
        if (!list || !merchantId) return;
        setUploading(true);
        try {
            // The server parses; the count is real, not a guess from the file
            // size. A file that is not a WhatsApp export comes back as its own
            // `failed` row with a merchant-readable reason, and the others
            // still go through.
            const rows = await uploadTranscripts(merchantId, Array.from(list));
            setFiles((prev) => [...prev, ...rows]);
            setAnalysis({ kind: "idle" });
            setProblems([]);
        } catch {
            snackbar.showError("Could not upload the chats. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const parsed = files.filter((f) => f.status === "parsed");

    const analyse = async () => {
        if (!merchantId || !parsed.length) return;
        setProblems([]);
        setAnalysis({ kind: "running", jobId: null });
        try {
            const result = await analyseTranscripts(merchantId, parsed.map((f) => f.id));
            if (result.status === "done") setAnalysis({ kind: "done", threadCount: result.threadCount, proposals: result.proposals });
            else { pollJob.current = result.jobId; setAnalysis({ kind: "running", jobId: result.jobId }); }
        } catch {
            setAnalysis({ kind: "idle" });
            snackbar.showError("Could not read the chats. Please try again.");
        }
    };

    // A 202 means the analysis is still running server-side: ask every three
    // seconds until it says done. Cleared whenever the job changes or the
    // screen goes away, so a stale poll cannot land on a newer analysis.
    useEffect(() => {
        if (analysis.kind !== "running" || !analysis.jobId || !merchantId) return;
        const jobId = analysis.jobId;
        const t = window.setInterval(async () => {
            try {
                const result = await getAnalysisJob(merchantId, jobId);
                if (pollJob.current !== jobId) return;
                if (result.status === "done") {
                    pollJob.current = null;
                    setAnalysis({ kind: "done", threadCount: result.threadCount, proposals: result.proposals });
                }
            } catch {
                // One failed poll is not a failed analysis; the next tick asks again.
            }
        }, POLL_MS);
        return () => window.clearInterval(t);
    }, [analysis, merchantId]);

    const proposals = analysis.kind === "done" ? analysis.proposals : [];
    const acceptedIds = proposals.filter((p) => p.accepted).map((p) => p.id);

    const toggleProposal = (id: string, accepted: boolean) =>
        setAnalysis((cur) => cur.kind === "done"
            ? { ...cur, proposals: cur.proposals.map((p) => (p.id === id ? { ...p, accepted } : p)) }
            : cur);

    const apply = async () => {
        if (!merchantId || !acceptedIds.length || analysis.kind !== "done") return;
        setApplying(true);
        setProblems([]);
        try {
            // Only the ticked ids. The server writes those and nothing else,
            // and answers with both updated documents.
            const result = await applyProposals(merchantId, acceptedIds);
            if (result.ok) {
                const byKind = proposals.filter((p) => acceptedIds.includes(p.id)).reduce<Record<string, number>>((acc, p) => {
                    acc[p.kind] = (acc[p.kind] ?? 0) + 1; return acc;
                }, {});
                const summary = Object.entries(byKind).map(([k, n]) => `${n} ${PROPOSAL_KIND_LABELS[k as Proposal["kind"]].toLowerCase()}${n === 1 ? "" : "s"}`).join(", ");
                setAnalysis({ kind: "applied", merchant: result.merchant, flow: result.flow, count: acceptedIds.length });
                snackbar.showSuccess(`Applied ${acceptedIds.length} ${acceptedIds.length === 1 ? "change" : "changes"}.`, summary);
            } else if (result.kind === "invalid") {
                // Nothing was written. The validator's own words, and the
                // list stays exactly as the merchant left it.
                setProblems(result.problems);
            } else {
                snackbar.showError(result.message);
            }
        } finally {
            setApplying(false);
        }
    };

    const remove = async () => {
        if (!merchantId) return;
        setDeleting(true);
        try {
            await deleteTranscripts(merchantId);
            setFiles([]);
            setAnalysis({ kind: "idle" });
            setProblems([]);
            setConfirmDelete(false);
            snackbar.showSuccess("Uploaded chats deleted.");
        } catch {
            snackbar.showError("Could not delete the chats. Please try again.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div>
            <PageHeader
                title="Learn from your chats"
                subtitle="Upload conversations you have already had, and we will suggest the flow, tone and answers you are using today."
                action={
                    analysis.kind === "done" ? (
                        <Button disabled={acceptedIds.length === 0 || applying} onClick={() => void apply()}>
                            {applying ? "Applying…" : `Apply ${acceptedIds.length} ${acceptedIds.length === 1 ? "change" : "changes"}`}
                        </Button>
                    ) : undefined
                }
            />

            <div className="mb-5 grid items-start gap-5 lg:grid-cols-2">
            <Card>
                <CardHeader
                    title="Upload threads"
                    icon={UploadIcon}
                    description="In WhatsApp open a chat, tap the contact name, then Export Chat, and choose Without Media. Six to a dozen threads is plenty."
                    aside={files.length > 0 ? (
                        confirmDelete ? (
                            <span className="flex items-center gap-2 text-xs">
                                <span className="text-gray-700">Delete {files.length} uploaded {files.length === 1 ? "chat" : "chats"} now?</span>
                                <Button size="sm" variant="destructive" onClick={() => void remove()} disabled={deleting}>{deleting ? "Deleting…" : "Delete"}</Button>
                                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>Keep</Button>
                            </span>
                        ) : (
                            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                                <Trash2Icon className="mr-1 h-3.5 w-3.5" /> Delete uploaded chats
                            </Button>
                        )
                    ) : undefined}
                />
                <CardBody className="pt-0">
                    <label className={cn("flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-8 text-center hover:bg-gray-50", uploading && "pointer-events-none opacity-60")}>
                        {uploading ? <Loader2Icon className="h-6 w-6 animate-spin text-gray-400" /> : <UploadIcon className="h-6 w-6 text-gray-400" />}
                        <span className="text-sm font-medium text-gray-700">{uploading ? "Uploading…" : "Choose exported chats"}</span>
                        <span className="text-xs text-gray-500">.txt or .zip from WhatsApp · up to 20 files, 10 MB each</span>
                        <input
                            type="file"
                            multiple
                            accept=".txt,.zip"
                            className="hidden"
                            disabled={uploading}
                            onChange={(e) => { void addFiles(e.target.files); e.target.value = ""; }}
                        />
                    </label>

                    {files.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                            {files.map((f) => (
                                <div key={f.id} className={cn("flex items-center gap-2.5 rounded-md border px-3 py-2", f.status === "failed" ? "border-rose-200 bg-rose-50" : "border-gray-200")}>
                                    {f.status === "parsing"
                                        ? <Loader2Icon className="h-4 w-4 shrink-0 animate-spin text-gray-400" />
                                        : f.status === "failed"
                                            ? <AlertTriangleIcon className="h-4 w-4 shrink-0 text-rose-600" />
                                            : <FileTextIcon className="h-4 w-4 shrink-0 text-gray-400" />}
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm text-gray-800">{f.name}</span>
                                        {f.status === "failed" && f.error && <span className="block text-xs text-rose-700">{f.error}</span>}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {f.status === "parsed" ? `${f.messageCount} messages` : f.status === "parsing" ? "Reading…" : "Not used"}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}
                                        className="rounded p-0.5 text-gray-400 hover:text-gray-700"
                                        aria-label={`Remove ${f.name}`}
                                    >
                                        <XIcon className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                            <div className="pt-2">
                                <Button onClick={() => void analyse()} disabled={analysis.kind === "running" || parsed.length === 0}>
                                    {analysis.kind === "running"
                                        ? <><Loader2Icon className="mr-1.5 h-4 w-4 animate-spin" /> Reading your chats…</>
                                        : `Analyse ${parsed.length} ${parsed.length === 1 ? "thread" : "threads"}`}
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="mt-4 flex items-start gap-2 rounded-xl bg-gray-50 px-3 py-2.5">
                        <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                        <p className="text-xs text-gray-600">
                            These are your customers' conversations. We use them once to set up your receptionist,
                            never to train a model, and delete them after {TRANSCRIPT_RETENTION_DAYS} days. Customer
                            names and numbers are removed as they are read.
                        </p>
                    </div>
                </CardBody>
            </Card>

            {/* The right column: what the upload is for while there is nothing
                to show, and out of the way once the proposals arrive. */}
            {analysis.kind !== "done" && (
                <Card>
                    <CardHeader title="What this finds" description="Read once, then turned into suggestions you approve." />
                    <CardBody className="pt-0">
                        <ul className="list-plain space-y-3">
                            {WHAT_IT_FINDS.map(({ icon: Icon, title, body }) => (
                                <li key={title} className="flex items-start gap-3">
                                    <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8F1FF] text-[#0A5FD1]">
                                        <Icon className="h-4 w-4 text-blue-600" />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-sm font-medium text-gray-900">{title}</span>
                                        <span className="block text-xs text-gray-500">{body}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                        {files.length === 0 && (
                            <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500">
                                Most merchants already run a consistent flow by hand. This finds it rather than
                                making you type it in.
                            </p>
                        )}
                    </CardBody>
                </Card>
            )}
            </div>

            {problems.length > 0 && (
                <ul className="list-plain mb-5 space-y-1 rounded-xl bg-amber-50 ring-1 ring-inset ring-amber-200 px-4 py-3">
                    <li className="text-xs font-medium text-amber-900">These changes could not be applied together. Nothing was changed.</li>
                    {problems.map((e) => <li key={e} className="text-xs text-amber-800">{e}</li>)}
                </ul>
            )}

            {analysis.kind === "applied" ? (
                <Card>
                    <CardHeader title="Applied" description={`${analysis.count} ${analysis.count === 1 ? "change is" : "changes are"} now in your setup.`} />
                    <CardBody className="pt-0 text-sm text-gray-700">
                        {/* From the returned documents, not a refetch: the merchant
                            now has {n} services and {n} FAQ notes, and the flow is
                            at version {v} with {n} steps. */}
                        <p className="my-0">
                            Services now lists {analysis.merchant.services.length} {analysis.merchant.services.length === 1 ? "service" : "services"} and {analysis.merchant.faqNotes.length} FAQ {analysis.merchant.faqNotes.length === 1 ? "note" : "notes"};
                            the conversation is version {analysis.flow.version} with {analysis.flow.steps.length} {analysis.flow.steps.length === 1 ? "step" : "steps"}.
                            Edit or undo any of it on those pages.
                        </p>
                    </CardBody>
                </Card>
            ) : analysis.kind === "done" ? (
                <Card>
                    <CardHeader
                        title="What we found"
                        description={`From ${analysis.threadCount} ${analysis.threadCount === 1 ? "thread" : "threads"}. Tick what is right. Nothing changes until you apply it.`}
                        aside={<span className="text-xs text-gray-500">{acceptedIds.length} of {proposals.length} selected</span>}
                    />
                    <CardBody className="pt-0">
                        {proposals.map((p) => (
                            <label
                                key={p.id}
                                htmlFor={`proposal-${p.id}`}
                                className="flex cursor-pointer items-start gap-3 border-t border-gray-100 py-3 first:border-t-0 first:pt-0"
                            >
                                {/* `accepted` arrives as the server's suggested default —
                                    low confidence is always unticked — and the merchant
                                    changes it from there. */}
                                <Checkbox
                                    id={`proposal-${p.id}`}
                                    className="mt-1 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                                    checked={p.accepted}
                                    onCheckedChange={(v) => toggleProposal(p.id, v === true)}
                                />
                                <span className="flex-1">
                                    <span className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{p.title}</span>
                                        <TagPill>{PROPOSAL_KIND_LABELS[p.kind] ?? p.kind}</TagPill>
                                        <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", CONFIDENCE_STYLE[p.confidence] ?? CONFIDENCE_STYLE.low)}>
                                            {CONFIDENCE_LABEL[p.confidence] ?? p.confidence}
                                        </span>
                                    </span>
                                    <span className="mt-1 block text-xs text-gray-600">{p.detail}</span>
                                    {/* Optional, and post-redaction: "[phone]" and "[customer]"
                                        in a quote are correct output, rendered as plain text. */}
                                    {p.quote && (
                                        <span className="mt-1.5 block border-l-2 border-gray-200 pl-2.5 text-xs italic text-gray-500">
                                            “{p.quote}”
                                        </span>
                                    )}
                                    <span className="mt-1 block text-[11px] text-gray-400">
                                        Seen in {p.evidenceCount} {p.evidenceCount === 1 ? "thread" : "threads"}
                                    </span>
                                </span>
                            </label>
                        ))}
                        <p className="mt-3 flex items-start gap-1.5 border-t border-gray-100 pt-3 text-xs text-gray-500">
                            <CheckCircle2Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                            Applying adds these to your Conversation, Services and Behaviour pages. You can edit
                            or undo any of them there afterwards.
                        </p>
                    </CardBody>
                </Card>
            ) : null}
        </div>
    );
}
